import React, { useState } from 'react';
import { Upload, X, CheckCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';

const ImportPaiementsExcel = ({ API_BASE_URL, getAuthConfig, onImportSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  // Gérer la sélection du fichier
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        alert('❌ Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  // Lire et importer le fichier Excel
  const handleImport = async () => {
    if (!file) {
      alert('❌ Veuillez sélectionner un fichier');
      return;
    }

    setImporting(true);
    setResults(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        alert('❌ Le fichier est vide');
        setImporting(false);
        return;
      }

      // Importer chaque ligne
      const config = getAuthConfig();
      const resultats = {
        success: [],
        errors: []
      };

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const rowNumber = i + 2; // +2 car ligne 1 = entête

        try {
          // Validation des champs requis
          if (!row['Nom Complet Etudiant']) {
            throw new Error('Nom Complet Etudiant manquant');
          }
          if (!row['Classe']) {
            throw new Error('Classe manquante');
          }
          if (!row['Date Debut']) {
            throw new Error('Date Debut manquante');
          }
          if (row['Nombre Mois'] === undefined || row['Nombre Mois'] === null) {
            throw new Error('Nombre Mois manquant');
          }
          if (!row['Montant']) {
            throw new Error('Montant manquant');
          }
          if (!row['Numero Serie']) {
            throw new Error('Numero Serie manquant');
          }
          if (!row['Type Paiement']) {
            throw new Error('Type Paiement manquant');
          }
          // ✅ VALIDATION: Date de paiement
          if (!row['Date Paiement']) {
            throw new Error('Date Paiement manquante');
          }
          // ✅ VALIDATION: Mode de paiement
          if (!row['Mode Paiement']) {
            throw new Error('Mode Paiement manquant');
          }

          // Récupérer l'étudiant par nom complet
          const resEtudiants = await axios.get(`${API_BASE_URL}/etudiant`, config);
          const etudiant = resEtudiants.data.find(e => 
            e.nomComplet.toLowerCase().trim() === row['Nom Complet Etudiant'].toLowerCase().trim()
          );

          if (!etudiant) {
            throw new Error(`Étudiant "${row['Nom Complet Etudiant']}" introuvable`);
          }

          // Préparer les données du paiement
          const paiementData = {
            etudiant: etudiant._id,
            cours: [row['Classe']],
            moisDebut: row['Date Debut'],
            nombreMois: parseInt(row['Nombre Mois']) || 0,
            montant: parseFloat(row['Montant']),
            numeroSerie: row['Numero Serie'].toString().trim(),
            typePaiement: row['Type Paiement'].toLowerCase(),
            estInscription: row['Type Paiement'].toLowerCase() === 'inscription',
            note: row['Note'] || '',
            datePaiement: row['Date Paiement'],
            modePayment: row['Mode Paiement'] || null  // ✅ AJOUTÉ
          };

          // Envoyer le paiement
          await axios.post(`${API_BASE_URL}/paiements`, paiementData, config);

          resultats.success.push({
            ligne: rowNumber,
            etudiant: row['Nom Complet Etudiant'],
            montant: row['Montant'],
            serie: row['Numero Serie'],
            mode: row['Mode Paiement']  // ✅ AJOUTÉ
          });

        } catch (error) {
          resultats.errors.push({
            ligne: rowNumber,
            etudiant: row['Nom Complet Etudiant'] || 'N/A',
            erreur: error.response?.data?.error || error.message
          });
        }
      }

      setResults(resultats);
      
      if (resultats.success.length > 0 && onImportSuccess) {
        onImportSuccess();
      }

    } catch (error) {
      console.error('Erreur lecture fichier:', error);
      alert('❌ Erreur lors de la lecture du fichier Excel');
    } finally {
      setImporting(false);
    }
  };

  // Styles
  const styles = {
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: '#8b5cf6',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      padding: '30px',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
    },
    fileInput: {
      display: 'none'
    },
    fileLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '20px',
      border: '2px dashed #cbd5e0',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: '#f7fafc'
    },
    resultBox: {
      marginTop: '20px',
      padding: '15px',
      borderRadius: '8px',
      maxHeight: '300px',
      overflow: 'auto'
    },
    successBox: {
      backgroundColor: '#f0fdf4',
      border: '1px solid #bbf7d0'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      marginTop: '10px'
    },
    resultItem: {
      padding: '8px 0',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px'
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={styles.button}
      >
        <Upload size={18} />
        Importer Excel
      </button>

      {showModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileSpreadsheet size={24} color="#8b5cf6" />
                Importer des Paiements depuis Excel
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setFile(null);
                  setResults(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} color="#6b7280" />
              </button>
            </div>

            <div style={{ backgroundColor: '#fef3c7', padding: '15px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
              <strong style={{ color: '#92400e' }}>Colonnes requises:</strong>
              <ul style={{ marginTop: '10px', marginLeft: '20px', color: '#78350f', lineHeight: '1.8' }}>
                <li><strong>Nom Complet Etudiant</strong> (texte)</li>
                <li><strong>Classe</strong> (texte)</li>
                <li><strong>Date Debut</strong> (YYYY-MM-DD)</li>
                <li><strong>Nombre Mois</strong> (nombre)</li>
                <li><strong>Montant</strong> (nombre)</li>
                <li><strong>Numero Serie</strong> (texte unique)</li>
                <li><strong>Type Paiement</strong> (formation ou inscription)</li>
                <li><strong>Date Paiement</strong> (YYYY-MM-DD)</li>
                <li><strong>Mode Paiement</strong> (Espèce, Chèque encaissé, Versement, Virement, Chèque non encaissé)</li>
                <li><strong>Note</strong> (texte, optionnel)</li>
              </ul>
            </div>

            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={styles.fileInput}
              id="excel-file-input"
            />

            <label
              htmlFor="excel-file-input"
              style={{
                ...styles.fileLabel,
                borderColor: file ? '#8b5cf6' : '#cbd5e0',
                backgroundColor: file ? '#f3e8ff' : '#f7fafc'
              }}
            >
              <Upload size={24} color={file ? '#8b5cf6' : '#6b7280'} />
              <div>
                {file ? (
                  <>
                    <strong>{file.name}</strong>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Cliquez pour changer de fichier
                    </div>
                  </>
                ) : (
                  <>
                    <strong>Cliquez pour sélectionner un fichier Excel</strong>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Formats acceptés: .xlsx, .xls
                    </div>
                  </>
                )}
              </div>
            </label>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleImport}
                disabled={!file || importing}
                style={{
                  ...styles.button,
                  backgroundColor: '#10b981',
                  flex: 1,
                  opacity: !file || importing ? 0.5 : 1,
                  cursor: !file || importing ? 'not-allowed' : 'pointer'
                }}
              >
                <Upload size={18} />
                {importing ? 'Importation...' : 'Importer'}
              </button>
            </div>

            {results && (
              <div>
                {results.success.length > 0 && (
                  <div style={{ ...styles.resultBox, ...styles.successBox }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#15803d', fontWeight: 'bold' }}>
                      <CheckCircle size={20} />
                      {results.success.length} paiement(s) importé(s) avec succès
                    </div>
                    {results.success.map((item, idx) => (
                      <div key={idx} style={styles.resultItem}>
                        <strong>Ligne {item.ligne}:</strong> {item.etudiant} - {item.montant} MAD ({item.serie}) - Mode: {item.mode}
                      </div>
                    ))}
                  </div>
                )}

                {results.errors.length > 0 && (
                  <div style={{ ...styles.resultBox, ...styles.errorBox }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#dc2626', fontWeight: 'bold' }}>
                      <AlertCircle size={20} />
                      {results.errors.length} erreur(s)
                    </div>
                    {results.errors.map((item, idx) => (
                      <div key={idx} style={styles.resultItem}>
                        <strong>Ligne {item.ligne}:</strong> {item.etudiant} - {item.erreur}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImportPaiementsExcel;